import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { usePartnerAuth } from '../contexts/PartnerAuthContext';
import Button from './ui/Button';

interface PartnerProtectedRouteProps {
  children?: React.ReactNode;
}

const PartnerProtectedRoute: React.FC<PartnerProtectedRouteProps> = ({ children }) => {
  const { partner, loading, signOut } = usePartnerAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // CRITICAL: Partners authenticate independently via PartnerAuthContext
  // They do NOT use Supabase auth.users - they use partners table email_login/encrypted_password
  if (!partner) {
    return <Navigate to="/partner-login" replace />;
  }

  // Check if partner account is active
  if (partner.status !== 'active') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-yellow-50 to-orange-100 p-6">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
              <div className="text-3xl">⚠️</div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Inactive</h2>
            <p className="text-gray-600 mb-4">
              Your partner account status is: <span className="font-semibold">{partner.status}</span>
            </p>
            <p className="text-gray-500 text-sm mb-6">
              {partner.status === 'pending' && 'Your application is being reviewed. You will receive an email once approved.'}
              {partner.status === 'suspended' && 'Your account has been suspended. Please contact support for assistance.'}
              {partner.status === 'rejected' && 'Your application was not approved. Please contact support for more information.'}
              {!['pending', 'suspended', 'rejected', 'active'].includes(partner.status) && 'Please contact support for assistance.'}
            </p>
            <div className="flex flex-col gap-3 w-full">
              <Button
                variant="default"
                iconName="LogOut"
                iconPosition="left"
                fullWidth
                onClick={async () => {
                  await signOut();
                  window.location.href = '/partner-login';
                }}
              >
                Logout
              </Button>
              <Button
                variant="outline"
                iconName="Home"
                iconPosition="left"
                fullWidth
                onClick={() => window.location.href = '/partner-login'}
              >
                Back to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Partner is authenticated and active - render protected content
  return <>{children ? children : <Outlet />}</>;
};

export default PartnerProtectedRoute;